import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { Toaster } from '@/components/ui/sonner'
import { AppSidebar } from '@/components/shell/app-sidebar'
import { Topbar } from '@/components/shell/topbar'
import { UiStateProvider } from '@/components/shell/ui-state'
import { useUiState } from '@/hooks/use-ui-state'
import { IssueSheet } from '@/components/sheet/issue-sheet'
import { CommandPalette } from '@/components/palette/command-palette'
import { NewIssueDialog } from '@/components/new-issue/new-issue-dialog'
import { getSidebarOpen, setLastView, setSidebarOpen as persistSidebarOpen } from '@/lib/persistence'

function GlobalHotkeys() {
  const { setPaletteOpen, paletteOpen } = useUiState()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setPaletteOpen(!paletteOpen)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [paletteOpen, setPaletteOpen])
  return null
}

function NativeMenuEscape() {
  useEffect(() => {
    // Shift+right-click is the escape hatch to the browser's own context menu
    // (its native "Open in new tab", "Save link as", inspect, …). Stopping the
    // event in the capture phase keeps it from reaching React/Radix, which would
    // otherwise preventDefault it and open our custom menu instead.
    const onContextMenu = (e: MouseEvent) => {
      if (e.shiftKey) e.stopPropagation()
    }
    document.addEventListener('contextmenu', onContextMenu, true)
    return () => document.removeEventListener('contextmenu', onContextMenu, true)
  }, [])
  return null
}

function LastViewRecorder() {
  const { pathname, search } = useLocation()
  useEffect(() => {
    // Issue pages aren't views — recording them would make "back to last
    // view" (page collapse, post-delete) bounce to the issue itself.
    if (pathname !== '/' && !pathname.startsWith('/issues/')) {
      setLastView(pathname + search)
    }
  }, [pathname, search])
  return null
}

export default function AppLayout() {
  // Own the open state: SidebarProvider ignores its internal state once an
  // onOpenChange callback is supplied, so it must be fully controlled.
  const [sidebarOpen, setSidebarOpen] = useState(getSidebarOpen)
  return (
    <UiStateProvider>
      <SidebarProvider
        open={sidebarOpen}
        onOpenChange={(open) => {
          setSidebarOpen(open)
          persistSidebarOpen(open)
        }}
      >
        <AppSidebar />
        <SidebarInset className="flex h-svh min-w-0 flex-col overflow-hidden">
          <Topbar />
          <div className="min-h-0 flex-1">
            <Outlet />
          </div>
        </SidebarInset>
        <GlobalHotkeys />
        <NativeMenuEscape />
        <LastViewRecorder />
        <IssueSheet />
        <CommandPalette />
        <NewIssueDialog />
        <Toaster position="bottom-right" />
      </SidebarProvider>
    </UiStateProvider>
  )
}
